import { useEffect, useMemo, useState } from 'react'
import { Modal, Checkbox, Notice } from '@pretamanger/component-library'
import {
  LabelVeganOption,
  ModalContent,
  ModalText,
  ModalDescription,
  ModalTitle,
  FilterVegan,
  ButtonWrapper,
  ButtonOption,
  AllergensWrapper,
  ModalCloseButton,
  ModalCross
} from './styles'
import {
  allergensList,
  suitablesList,
  allList,
  allNut,
  allGluten
} from './const'
import { useAllergensQuery } from '#src/auth/my-account-page/hooks/use-allergens-query'
import { useGetTranslation } from '#src/auth/my-account-page/hooks/use-get-translation'

export const FilterCheckbox = ({
  label,
  lists,
  isNotHidden = false,
  onChange = (e, n) => {}
}) => {
  return (
    <FilterVegan isNotHidden={isNotHidden}>
      <LabelVeganOption>{label}</LabelVeganOption>
      {lists?.map((item, index) => {
        return (
          <Checkbox
            key={index}
            id={item?.name}
            label={item?.name}
            name={item?.name}
            required={false}
            onChange={e => onChange(item?.name, e?.target)}
            checked={item?.checked}
          />
        )
      })}
    </FilterVegan>
  )
}

const FilterAllegens = ({ lists, onChange = (e, n) => {} }) => {
  return (
    <AllergensWrapper>
      {lists?.map((item, index) => {
        return (
          <Checkbox
            key={index}
            id={item?.name}
            label={item?.name}
            name={item?.name}
            required={false}
            onChange={e => onChange(item?.name, e?.target)}
            checked={item?.checked}
          />
        )
      })}
    </AllergensWrapper>
  )
}

const ModalClose = ({ onClose }) => {
  return (
    <ModalCloseButton
      title='close'
      className='custom-modal-close-button'
      onClick={onClose}
    >
      <ModalCross colour={'grey-700'} />
    </ModalCloseButton>
  )
}

export const mapListFilter = (list, arrList) => {
  return list?.map(item => {
    const checked = arrList?.includes(item)
    return { name: item, checked }
  })
}

const FilterAllergensModal = ({ isOpen, onClose }) => {
  const {
    updateFilterMultipleQuery,
    createListString,
    createListStringFromList,
    suitables: suitablesQuery,
    allergens: allergensQuery
  } = useAllergensQuery()
  const [suitables, setSuitables] = useState('')
  const [allergens, setAllergens] = useState('')
  const getTranslation = useGetTranslation()
  const text = {
    title: getTranslation('filter-allergens-title'),
    description: getTranslation('filter-allergens-description'),
    labelVegan: getTranslation('filter-allergens-label-vegan'),
    labelContains: getTranslation('filter-allergens-contains'),
    info: getTranslation('filter-allergens-info')
  }

  useEffect(() => {
    setSuitables(suitablesQuery)
  }, [suitablesQuery])

  useEffect(() => {
    setAllergens(allergensQuery)
  }, [allergensQuery])

  const listsVegan = useMemo(() => {
    const valueSuitables = suitables
    return mapListFilter(suitablesList, valueSuitables)
  }, [suitables])

  const listsContaining = useMemo(() => {
    const valueAllergens = allergens
    return mapListFilter(allergensList, valueAllergens)
  }, [allergens])

  const clearStateAfterAppliedChanges = () => {
    setSuitables('')
    setAllergens('')
  }

  const onChangeSuitables = (name, eventTarget) => {
    const { checked } = eventTarget
    const action = checked ? 'add' : 'remove'
    const newListString = createListString({
      type: 'suitables',
      name,
      action,
      defaultList: suitables
    })
    setSuitables(newListString)
  }

  const onChangeAllergens = (name, eventTarget) => {
    const { checked } = eventTarget
    const action = checked ? 'add' : 'remove'
    let newListString = allergens

    if (name.includes(allList.all)) {
      let newNames = name
      if (name.includes(allList.nut)) {
        newNames = allNut
      } else if (name.includes(allList.gluten)) {
        newNames = allGluten
      }
      newListString = createListStringFromList({
        type: 'allergens',
        names: newNames,
        action,
        defaultList: newListString
      })
    } else {
      if (name.includes(allList.nut)) {
        newListString = createListString({
          type: 'allergens',
          name: allList.nutLabel,
          action: 'remove',
          defaultList: newListString
        })
      } else if (name.includes(allList.gluten)) {
        newListString = createListString({
          type: 'allergens',
          name: allList.glutenLabel,
          action: 'remove',
          defaultList: newListString
        })
      }
      newListString = createListString({
        type: 'allergens',
        name,
        action,
        defaultList: newListString
      })
    }

    setAllergens(newListString)
  }

  const applyFilter = () => {
    const newSuitables = suitables
    const newAllergens = allergens
    updateFilterMultipleQuery({
      allergens: newAllergens,
      suitables: newSuitables
    })
    onClose()
    clearStateAfterAppliedChanges()
  }

  const clearFilter = () => {
    updateFilterMultipleQuery({ allergens: '', suitables: '' })
    clearStateAfterAppliedChanges()
    onClose()
  }

  return (
    <Modal open={isOpen} size='wide' showCloseButton={false}>
      <ModalClose onClose={onClose} />
      <ModalContent data-testid='filter-allergen-modal'>
        <ModalText>
          <ModalTitle level='h2' styleOverride='headingSm'>
            {text.title}
          </ModalTitle>
          <ModalDescription>{text.description}</ModalDescription>
        </ModalText>
        <FilterCheckbox
          label={text.labelVegan}
          lists={listsVegan}
          onChange={onChangeSuitables}
        />
        <FilterCheckbox
          label={text.labelContains}
          lists={listsContaining}
          onChange={onChangeAllergens}
        />
        <FilterAllegens lists={listsContaining} onChange={onChangeAllergens} />
        <Notice title={text.info} variant='info' />
        <ButtonWrapper>
          <ButtonOption
            styleType='secondary'
            className='mr-3 md:mr-6'
            onClick={clearFilter}
            data-testid='clear-allergen-button'
          >
            Clear filters
          </ButtonOption>
          <ButtonOption
            styleType='primary'
            className='ml-3 md:ml-6'
            onClick={applyFilter}
            data-testid='apply-allergen-button'
          >
            Apply filters
          </ButtonOption>
        </ButtonWrapper>
      </ModalContent>
    </Modal>
  )
}

export default FilterAllergensModal
